import PageWrapper from "@/pages/PageWrapper.vue";
import PageWrapperAuth from "@/pages/PageWrapperAuth.vue";
import ErrorPage from "@/pages/ErrorPage.vue";
import LandingPage from "@/pages/LandingPage.vue";
import CookiePolicyPage from "@/pages/privacyTerms/CookiePolicyPage.vue";

// Auth pages
import SignUpPage from "@/pages/auth/signup/SignUpPage.vue";
import PasswordLostPage from "@/pages/auth/PasswordLostPage.vue";
import LoginPage from "@/pages/auth/LoginPage.vue";
import LoginCodePage from "@/pages/auth/LoginCodePage.vue";
// This is the page which is linked to by admins with 'impersonation' permissions
// (e.g. HCPs) to complete a flow on behalf of someone else
import ImpersonationFlowAccess from "@/pages/auth/ImpersonationFlowAccess.vue";

// Logged out visible pages
import PrivacyPage from "@/pages/privacyTerms/PrivacyPage.vue";
import TermsPage from "@/pages/privacyTerms/TermsPage.vue";
// import Reports from "./pages/reports";

import BespokePage from "@/pages/content/BespokePage.vue";

// Logged in only pages
import BlogPage from "@/pages/content/BlogPage.vue";
import HomePage from "@/pages/loggedIn/HomePage.vue";
import AccountPage from "@/pages/loggedIn/AccountPage.vue";
import DocumentPage from "@/pages/loggedIn/DocumentPage.vue";
import FlowselPage from "@/pages/FlowselPage.vue";
import ReturnTestPage from "@/pages/returnDNATest/ReturnTestPage.vue";
import ContributionsExternalPage from "@/pages/loggedIn/ContributionsExternalPage.vue";
import StudyPage from "@/pages/StudyPage.vue";
import StudyAccessPage from "@/pages/StudyAccessPage.vue";
import TraitReportPage from "@/pages/loggedIn/TraitReportPage.vue";
import type { RouteRecordRaw } from "vue-router";
import { clientSettings } from "@/plugins/useClientSettings";

function getRoutes(): RouteRecordRaw[] {
   const baseRoutes: Array<RouteRecordRaw> = [
      // Logged in only routes
      // -----------------------------------------
      { path: "home", name: "home", component: HomePage },
      { path: "account", name: "account", component: AccountPage },
      // Documents
      { path: "documents", name: "documents", redirect: { name: "account", query: { scrollTo: "documents" } } },
      { path: "document/:key", name: "document", component: DocumentPage },
      // currently there is not enough to add to the settings page so we will just redirect to account
      { path: "settings", name: "settings", redirect: { name: "account" } },

      // feature flag routes
      // personalised trait reports
      {
         path: "report/:key",
         name: "report",
         component: TraitReportPage,
         beforeEnter: (to, from) => {
            // feature not enabled > dont allow route access
            if (!clientSettings.traitReports.isActive) {
               return { name: "home", params: { locale: to.params?.locale } };
            }
         },
      },

      // external contributions
      {
         path: "contributions",
         name: "contributions",
         component: ContributionsExternalPage,
         beforeEnter: (to, from) => {
            // feature not enabled > dont allow route access
            if (!clientSettings.externalContributions.isActive) {
               return { name: "home", params: { locale: to.params?.locale } };
            }
         },
      },

      // Landing & Content routes
      // -----------------------------------------
      { path: "", component: LandingPage, name: "landing", meta: { isPublic: true } },

      { path: "blog/:key", name: "blogpost", component: BlogPage, meta: { isPublic: true } },

      {
         path: "return-test",
         name: "return-test",
         component: ReturnTestPage,
         meta: { isPublic: true },
      },

      // Study page
      { path: "study/:key", name: "study", component: StudyPage, meta: { isPublic: true } },

      // Bespoke client pages (for now these will be set to public access as first use case is flf/findhelp)
      { path: "b/:key", name: "bespokepage", component: BespokePage, meta: { isPublic: true } },

      // Privacy, terms, cookies
      { path: "privacy", name: "privacy", component: PrivacyPage, meta: { isPublic: true } },
      { path: "terms", name: "terms", component: TermsPage, meta: { isPublic: true } },
      { path: "cookie-policy", name: "cookiepolicy", component: CookiePolicyPage, meta: { isPublic: true } },
   ];

   const authRoutes: Array<RouteRecordRaw> = [
      // Authentication routes
      // -----------------------------------------
      { path: "signup", name: "signup", component: SignUpPage, meta: { isPublic: true, loggedOutOnly: true } },
      // /register redirects to /signup
      { path: "register", redirect: { name: "signup" } },
      {
         path: "complete/:token",
         name: "register-complete",
         // route level code-splitting - this generates a separate chunk (About.[hash].js) for this route
         // to be lazy-loaded when the route is visited (needed here because of large password related libraries)
         component: () => import("@/pages/auth/RegisterCompletePage.vue"),
         // component: RegisterCompletePage,
         meta: { isPublic: true, loggedOutOnly: true },
      },
      // password lost - enter an email address to send a password reset link
      { path: "password-lost", name: "password-lost", component: PasswordLostPage, meta: { isPublic: true } },
      // resetpass - after clicking the reset link in the email, this is the page to enter a new password
      {
         path: "resetpass/:token",
         name: "password-reset",
         // route level code-splitting - this generates a separate chunk (About.[hash].js) for this route
         // to be lazy-loaded when the route is visited (needed here because of large password related libraries)
         // used here because of the large password-strength-check library required on this page
         component: () => import("@/pages/auth/PasswordResetPage.vue"),
         meta: { isPublic: true },
      },
      { path: "login", name: "login", component: LoginPage, meta: { isPublic: true, loggedOutOnly: true } },
      {
         path: "device-code",
         name: "device-code",
         component: LoginCodePage,
         meta: { isPublic: true },
      },
      {
         path: "access/flow/:flow_key/:token",
         name: "access-complete",
         component: ImpersonationFlowAccess,
         meta: { title: "Access", isPublic: true },
      },
   ];

   const localeParam = "/:locale([a-zA-Z]{2}-[a-zA-z]{2})?"; // matches two letters, dash, two more letters (e.g., en-gb)
   return [
      // The cookie bar must appear on all page types in this list
      // -----------------------------------------

      // Pages which don't have a header/footer so don't use a wrapper
      // -----------------------------------------
      // Flows - entry subject to navigation guard to check access see flowAccessMiddleware in routes-config.ts
      {
         path: `${localeParam}/flow/:id`,
         name: "flow",
         component: FlowselPage,
         meta: { isPublic: true },
      },

      // Study access page for invite-only studies
      { path: `${localeParam}/study/:key/study-access`, name: "study-access", component: StudyAccessPage },

      // Page Wrappers
      // -----------------------------------------
      // default routes
      {
         path: localeParam,
         name: "",
         component: PageWrapper,
         children: baseRoutes,
      },
      // auth routes
      {
         path: localeParam,
         name: "",
         component: PageWrapperAuth,
         children: authRoutes,
      },
      // Error page -----------------------------------------
      {
         name: "error-page",
         path: `${localeParam}/error`,
         component: ErrorPage,
         meta: { isPublic: true },
         // Use a function to map query parameters to props
         props: (route) => ({
            errorType: route.query.error_type,
         }),
      },
      // -----------------------------------------
      {
         path: `${localeParam}/:pathMatch(.*)*`,
         name: "not-found",
         component: ErrorPage,
         props: { errorType: "page_not_found" },
         meta: { isPublic: true },
      },
   ];
}

export { getRoutes };
